import { z } from "zod";

import { FormField } from "@components/FormEngine/types";

import { PersonalSleepHistoryOptions } from "@constants/PatientFormConstants";

let AMs = []; // ['12:00 AM', '12:30 AM', '1:00 AM', ..., '11:30 AM']
let PMs = []; // ['12:00 PM', '12:30 PM', '1:00 PM', ..., '11:30 PM']
for (const hr of Array(12)
  .fill(null)
  .map((_, i) => (((i - 12) % 12) + 12).toString())) {
  for (const min of ["00", "30"]) {
    AMs.push(`${hr}:${min} AM`);
    PMs.push(`${hr}:${min} PM`);
  }
}
const startWithAM = AMs.concat(PMs);
const startWithPM = PMs.concat(AMs);

export const baseSleepQuestionnaireSchema: FormField[] = [
  {
    // Average Hours Slept (must allow dash)
    type: "input",
    name: "sleepQuestionnaire.averageHoursSlept",
    label: "Average Hours Slept",
    title: "Average Hours Slept",
    fieldProps: {
      sx: {
        width: { xs: "100%", md: "50%" },
      },
    },
    description: "How many hours do you sleep each night on average?",
    rule: z.string().regex(/\d{1,2}(\.\d){0,1}(-\d{1,2}(\.\d){0,1}){0,1}/),
  },
  {
    // Average Asleep Time
    type: "select",
    name: "sleepQuestionnaire.averageBedtime",
    label: "Average Bedtime",
    title: "Average Bedtime",
    fieldProps: {
      options: startWithPM.map((h) => ({ label: h, value: h })),
    },
    rule: z.string().min(1, "Average bedtime is required"),
  },
  {
    // Average Asleep Time
    type: "select",
    name: "sleepQuestionnaire.averageRiseTime",
    label: "Average Wakeup Time",
    title: "Average Wakeup Time",
    fieldProps: {
      options: startWithAM.map((h) => ({ label: h, value: h })),
    },
    rule: z.string().min(1, "Average wakeup time is required"),
  },
  {
    // Average Minutes to Doze (must allow dash)
    type: "input",
    name: "sleepQuestionnaire.averageMinutesToDoze",
    label: "Average Minutes to Doze",
    title: "Average Minutes to Doze",
    fieldProps: {
      sx: {
        width: { xs: "100%", md: "50%" },
      },
    },
    description: "How many minutes does it take you to doze off each night on average?",
    rule: z.string().regex(/\d{1,3}(-\d{1,3}){0,1}/),
  },
  {
    // Average Number of Wake Ups
    type: "input",
    name: "sleepQuestionnaire.numberOfAwakenings",
    label: "Average Number of Wake Ups",
    title: "Average Number of Wake Ups",
    fieldProps: {
      sx: {
        width: { xs: "100%", md: "50%" },
      },
    },
    description: "How many times do you wake up thoughout a typical night?",
    rule: z.string().regex(/\d{1,2}(-\d{1,2}){0,1}/),
  },
];

export const sleepQuestionnaireSchema: FormField[] = [
  ...baseSleepQuestionnaireSchema,
  {
    // Personal Sleep History
    type: "multi-select",
    name: "sleepQuestionnaire.personalSleepHistory",
    title: "Personal Sleep History",
    description: "Select all of the following you have experienced in the past year.",
    options: PersonalSleepHistoryOptions,
  },
  {
    type: "multi-select",
    name: "sleepQuestionnaire.sleepBehaviors",
    title: "Sleep Behaviors",
    description:
      "Select all of the following sleep bahaviors you or someone has noted about you in the past year.",
    options: [
      "Snoring",
      "Sleepwalking",
      "Sleep talking",
      "Heart Palpitations",
      "Grinding your teeth",
      "Twitching of the legs or arms",
      "Waking up with frequent urge to urinate",
      "Rolling or rocking movement",
      "Shouting, screaming, or swearing",
      "Violent movements",
      "Waking up with anxiety or tension",
      "Excessive sweating",
      "Waking up with chest pain",
      "Waking up with jaw pain",
      "Waking up gasping or choking",
      "Waking up with headache",
      "Waking up with heartburn",
      "Waking up with frightening images",
      "Waking up with air hunger (shortness of breath)",
      "Waking up with the feeling of weight on chest",
      "Apnea (i.e. lapses in breathing, periods of no breathing)",
      "Frequent coughing",
      "Falling out of bed",
      "Bed-wetting",
      "Large body jerks",
      "Loud snoring",
      "Restless sleep",
      "Waking up with terror",
    ].map((behavior) => ({ value: behavior, label: behavior })),
  },
];

export default sleepQuestionnaireSchema;
